<template>
  <div class="fu-fc" data-test="fu-cell-followup" :data-state="fu.state" :data-editable="fu.editable ? 'true' : 'false'">
    <span v-if="fu.state === 'saving'" class="fu-fc__btn fu-fc__btn--saving" role="status">
      <fu-icon name="spin" :size="14" :stroke="2.4" class="fu-fc__spin" />
      Saving…
    </span>

    <span v-else-if="fu.state === 'resolved'" class="fu-fc__dash" title="Nothing left to follow up" aria-label="Nothing left to follow up">—</span>

    <span v-else-if="isSetState && !clickable" class="fu-fc__muted">Not set</span>

    <component
      :is="clickable ? 'button' : 'span'"
      v-else
      :type="clickable ? 'button' : null"
      class="fu-fc__btn"
      :class="[`fu-fc__btn--${fu.state}`, { 'fu-fc__btn--static': !clickable }]"
      :aria-haspopup="clickable ? 'dialog' : null"
      :aria-expanded="clickable ? (expanded ? 'true' : 'false') : null"
      :aria-label="clickable ? ariaLabel : null"
      data-test="fu-followup-button"
      @click="onClick"
    >
      <template v-if="isSetState">
        <fu-icon name="plus" :size="15" />
        Set follow-up
      </template>
      <template v-else-if="fu.state === 'due'">
        <fu-icon name="phone" :size="14" />
        Due today
      </template>
      <template v-else-if="fu.state === 'past'">
        <fu-icon name="alert" :size="15" />
        {{ dateText }}
        <span class="fu-fc__rel">{{ relText }}</span>
      </template>
      <template v-else-if="fu.state === 'upcoming'">
        <fu-icon name="cal" :size="15" class="fu-fc__cal" />
        {{ dateText }}
        <span class="fu-fc__rel">{{ relText }}</span>
      </template>
      <template v-else-if="fu.state === 'stopped'">
        <fu-icon name="ban" :size="15" />
        Do not follow up
      </template>
      <template v-else-if="fu.state === 'done'">
        <fu-icon name="check" :size="15" />
        Follow-ups done
      </template>
      <fu-icon v-if="clickable" name="chev" :size="15" class="fu-fc__chev" />
    </component>

    <div v-if="hasMeta" class="fu-fc__meta" data-test="fu-followup-meta">
      <span
        v-if="fu.pastDue && fu.state !== 'past'"
        v-b-tooltip.hover.focus
        class="fu-fc__metaitem fu-fc__metaitem--danger"
        tabindex="0"
        title="Past Due!"
        data-test="fu-meta-pastdue"
      >
        <span class="fu-fc__pastdot" aria-hidden="true"></span>
        Past due
      </span>
      <span
        v-if="fu.hasInitialNote"
        v-b-tooltip.hover.focus
        class="fu-fc__metaitem"
        tabindex="0"
        :title="fu.initialNote"
        data-test="fu-meta-note"
      >
        <fu-icon name="note" :size="13" />
        Note
      </span>
      <span
        v-if="fu.overridden"
        v-b-tooltip.hover.focus
        class="fu-fc__metaitem"
        tabindex="0"
        title="This patient's follow-up date was overridden in the past."
        data-test="fu-meta-overridden"
      >
        <fu-icon name="history" :size="13" />
        Overridden
      </span>
      <span v-if="assignee" class="fu-fc__metaitem" data-test="fu-meta-assignee">
        <span class="fu-fc__asinit" aria-hidden="true">{{ assigneeInitials }}</span>
        <span class="fu-fc__sr">Assigned to</span>
        {{ assignee }}
      </span>
    </div>
  </div>
</template>

<script>
/**
 * The Follow-up cell — every state resolveFollowupState() returns (src/utils/followups/
 * followupState.js, which follows TODAY'S AdminTable, not the prototype), plus the meta line.
 *
 *   notset    dashed "+ Set follow-up" — for EVERYONE (legacy "Not Set" has no permission check)
 *   nodate    dashed "+ Set follow-up" when the user can edit (opens CHANGE-date mode, the legacy
 *             date dropdown); plain "Not set" when they cannot
 *   due       "Due today"            past      date + "Nd late"        upcoming  date + "in Nd"
 *   stopped   "Do not follow up"     done      "Follow-ups done"       resolved  a quiet dash
 *   saving    "Saving…"
 *
 * The cell is a button (opening the follow-up popover) when the user can edit it OR can restart
 * the cycle — legacy's restart icon ignores edit permission (kept, pinned by the agreement spec).
 * Anything else is a static pill. Meta line: past-due flag (the server's pastDue, when the date
 * itself does not already say so), the initial note, "overridden", and the assignee (aging).
 */
import FuIcon from './FuIcon.vue';
import { formatDate, daysBetween } from '@/utils/followups/rowModel.js';

export default {
  name: 'FollowupCell',
  components: { FuIcon },
  props: {
    fu: { type: Object, required: true },              // resolveFollowupState() result
    today: { type: String, required: true },           // 'YYYY-MM-DD', site zone
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    assignee: { type: String, default: '' },           // employee name — aging queues only
    expanded: { type: Boolean, default: false },       // its popover is open
  },
  computed: {
    isSetState() {
      return this.fu.state === 'notset' || this.fu.state === 'nodate';
    },
    clickable() {
      if (this.fu.state === 'saving' || this.fu.state === 'resolved') return false;
      return !!(this.fu.editable || this.fu.canRestart);
    },
    dateText() {
      return formatDate(this.fu.date, this.dateFormat);
    },
    days() {
      return daysBetween(this.today, this.fu.date);
    },
    relText() {
      const n = this.days;
      if (n === null) return '';
      return n < 0 ? `${-n}d late` : `in ${n}d`;
    },
    ariaLabel() {
      const what = {
        notset: 'Follow-up not set',
        nodate: 'No follow-up date',
        due: 'Follow-up due today',
        past: `Follow-up past due, ${this.dateText}`,
        upcoming: `Follow-up on ${this.dateText}`,
        stopped: 'Do not follow up',
        done: 'Follow-ups done',
      }[this.fu.state] || 'Follow-up';
      const action = this.fu.editable ? (this.isSetState ? 'Set follow-up' : 'Change follow-up') : 'Restart follow-ups';
      return `${what}. ${action}`;
    },
    hasMeta() {
      return !!((this.fu.pastDue && this.fu.state !== 'past') || this.fu.hasInitialNote || this.fu.overridden || this.assignee);
    },
    assigneeInitials() {
      return String(this.assignee || '').split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
    },
  },
  methods: {
    onClick(e) {
      if (!this.clickable) return;
      this.$emit('open', e.currentTarget);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-fc {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  min-width: 0;
}

.fu-fc__btn {
  @include fu-button-reset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 9px 0 11px;
  border-radius: 9px;
  border: 1px solid $fu-border-strong;
  background: $fu-surface;
  color: $fu-text;
  font-size: 13px;
  font-weight: $fu-fw-semibold;
  white-space: nowrap;
  max-width: 100%;
  transition: background $fu-dur-fast $fu-ease-out, border-color $fu-dur-fast $fu-ease-out;
  @include fu-focus-ring;
}

.fu-fc__btn--static {
  cursor: default;
  padding-right: 11px;
}

.fu-fc__btn--notset,
.fu-fc__btn--nodate {
  padding: 0 11px;
  border-style: dashed;
  border-color: $fu-neutral-300;
  color: $fu-neutral-600;
  &:hover { border-color: $fu-brand-400; color: $fu-brand-700; background: $fu-brand-50; }
}

.fu-fc__btn--upcoming {
  .fu-fc__cal { color: $fu-text-muted; }
  .fu-fc__rel { color: $fu-text-subtle; font-weight: $fu-fw-medium; }
  &:not(.fu-fc__btn--static):hover { background: $fu-neutral-50; }
}

.fu-fc__btn--due {
  border-color: $fu-brand;
  background: $fu-brand;
  color: #ffffff;
  font-weight: $fu-fw-bold;
  &:not(.fu-fc__btn--static):hover { background: $fu-brand-hover; }
  .fu-fc__chev { opacity: 0.8; }
}

.fu-fc__btn--past {
  border-color: $fu-danger-border;
  background: $fu-danger-subtle;
  color: $fu-danger-text;
  font-weight: $fu-fw-bold;
  &:not(.fu-fc__btn--static):hover { background: #fbe2e3; }
  .fu-fc__rel { font-weight: $fu-fw-medium; opacity: 0.85; }
}

.fu-fc__btn--stopped {
  border-color: $fu-border;
  background: $fu-neutral-100;
  color: $fu-neutral-700;
  &:not(.fu-fc__btn--static):hover { background: $fu-neutral-200; }
}

.fu-fc__btn--done {
  border-color: #c6e8d9;
  background: $fu-success-subtle;
  color: $fu-success;
  &:not(.fu-fc__btn--static):hover { background: #d9f0e5; }
}

.fu-fc__btn--saving {
  padding: 0 11px;
  border-color: $fu-border;
  background: $fu-neutral-50;
  color: $fu-text-muted;
  cursor: default;
}

.fu-fc__spin {
  @include fu-spin;
}

.fu-fc__chev {
  color: $fu-text-subtle;
  margin-left: -1px;
}

.fu-fc__muted {
  font-size: 13px;
  color: $fu-text-subtle;
}

.fu-fc__dash {
  font-size: 14px;
  color: $fu-neutral-300;
  padding-left: 2px;
}

.fu-fc__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  font-size: 11.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-muted;
}

.fu-fc__metaitem {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: $fu-radius-xs;
  cursor: default;
  @include fu-focus-ring;
}

.fu-fc__metaitem--danger {
  color: $fu-danger-text;
}

.fu-fc__pastdot {
  @include fu-status-dot($fu-danger);
  width: 7px;
  height: 7px;
}

.fu-fc__asinit {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
  font-size: 8.5px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.fu-fc__sr {
  @include fu-visually-hidden;
}
</style>
